import { createFragment, getElements } from "../utilities/elements.js";
import { getStringCssValue } from "../utilities/styles.js";
import { defineElement } from "./registry.js";
import { DickTokStandaloneComponent } from "./StandaloneComponent.js";

const CssVariables = Object.freeze({
  ANIMATION_DELAY: "--dt-stickyheader__animation-delay",
  ANIMATION_DISTANCE: "--dt-stickyheader__animation-distance",
  TOP: "--dt-stickyheader__top",
});

/**
 * UI component for a sticky header that can animate children on scroll.
 *
 * Descendant elements can also be individually animated on scroll by setting the
 * `animation-name` property and using `@keyframes` to define the initial style
 * (`from`) and collaped style (`to`).
 *
 * Uses the following CSS variables:
 *   * `--dt-stickyheader__top` (optional, default `0`)
 *   * `--dt-stickyheader__animation-distance` (required)
 *   * `--dt-stickyheader__background-color` (optional, default `rgba(0, 0, 0, 0)`)
 */
export class DickTokStickyHeader extends DickTokStandaloneComponent {
  static targets = ["slot"];

  static template = createFragment`
    <div class="dt-stickyheader__container">
        <slot data-target="slot"></slot>
    </div>`;

  #animationDistance = 0;
  #top = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setStyles();
    this.#setupCollapseAnimation();
  }

  #setStyles() {
    // Get the animation distance and top so we can calculate the scroll ratio
    this.#animationDistance = this.getActualCssValue(
      CssVariables.ANIMATION_DISTANCE,
    );
    this.#top = this.getActualCssValue(CssVariables.TOP);
    // Get all descendant elements in the slot
    const assignedElements = /** @type {HTMLSlotElement} */ (
      this.slotTarget
    ).assignedElements();
    const descendantElements = [
      ...assignedElements,
      ...assignedElements.flatMap((element) => [...getElements(element, "*")]),
    ];
    // Animation CSS properties are not inherited, set them on all descendents
    // with an animation-name so they can animate on collapse
    for (const element of descendantElements) {
      if (getStringCssValue(element, "animation-name") !== "none") {
        element.style.animationDelay = `var(${CssVariables.ANIMATION_DELAY})`;
        element.style.animationDuration = "1s";
        element.style.animationFillMode = "both";
        element.style.animationPlayState = "paused";
      }
    }
  }

  #setupCollapseAnimation() {
    document.addEventListener(
      "scroll",
      () => {
        const elementTop = this.getBoundingClientRect().top;
        const top = this.#animationDistance - elementTop - this.#top;
        const ratioScrolled = Math.min(top / (this.#top + elementTop), 1);
        this.style.setProperty(
          CssVariables.ANIMATION_DELAY,
          `-${ratioScrolled * 1000}ms`,
        );
      },
      { signal: this.disconnectSignal },
    );
  }
}

defineElement("dt-stickyheader", DickTokStickyHeader);
